// PendingFeeReportTable: Data grid view for pending fee records
import React, { useMemo } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
// MUI X DataGrid styles (needed for layout/visibility)
// import '@mui/x-data-grid/styles.css';
import { Box } from '@mui/material';
import pallet from '../../layouts/pallet';
import { UserStatus } from '../../types/userType';
import { dataGridSx } from '../common/styled/DataGrid';
import CustomDataGridToolbar from '../common/components/CustomDataGridToolbar';
import StatusLabel from '../common/components/StatusLabel';

export interface PendingFeeRecord {
  id: number;
  srNo: number;
  enrollmentNo: string;
  studentName: string;
  studentType: string;
  studentStatus: UserStatus;
  grade: string;
  division: string;
  academicYear: string;
  fathersName: string;
  contactNo: string;
  feeType: string;
  feePeriod: string;
  paymentDueDate: string;
  amount: number;
  lateFee: number;
  bounceCharges: number;
}

interface Props {
  data: PendingFeeRecord[];
}

const PendingFeeReportTable: React.FC<Props> = ({ data }) => {
  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'srNo', headerName: 'Sr. No.', width: 90, sortable: false },
      {
        field: 'enrollmentNo',
        headerName: 'Enrollment',
        flex: 1,
        minWidth: 140
      },
      {
        field: 'studentName',
        headerName: 'Student Name',
        flex: 1,
        minWidth: 180
      },
      {
        field: 'studentType',
        headerName: 'Student Type',
        flex: 1,
        minWidth: 130
      },
      {
        field: 'studentStatus',
        headerName: 'Student Status',
        flex: 1,
        minWidth: 130,
        renderCell: (params) => {
          const status = params.value as UserStatus;
          return <StatusLabel status={status} />;
        }
      },
      { field: 'grade', headerName: 'Grade', width: 100 },
      { field: 'division', headerName: 'Division', width: 110 },
      {
        field: 'academicYear',
        headerName: 'Academic Year',
        flex: 1,
        minWidth: 150
      },
      {
        field: 'fathersName',
        headerName: "Father's Name",
        flex: 1,
        minWidth: 170
      },
      { field: 'contactNo', headerName: 'Contact No.', flex: 1, minWidth: 130 },
      { field: 'feeType', headerName: 'Fee Type', flex: 1, minWidth: 140 },
      { field: 'feePeriod', headerName: 'Fee Period', flex: 1, minWidth: 130 },
      {
        field: 'paymentDueDate',
        headerName: 'Payment Due Date',
        flex: 1,
        minWidth: 170
      },
      {
        field: 'amount',
        headerName: 'Amount',
        flex: 1,
        minWidth: 120,
        type: 'number'
      },
      {
        field: 'lateFee',
        headerName: 'Late Fee',
        flex: 1,
        minWidth: 120,
        type: 'number'
      },
      {
        field: 'bounceCharges',
        headerName: 'Bounce Charges',
        flex: 1,
        minWidth: 140,
        type: 'number'
      }
    ],
    []
  );

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        autoHeight
        rows={data}
        columns={columns}
        disableRowSelectionOnClick
        showToolbar
        pagination
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10, page: 0 } }
        }}
        slots={{ toolbar: CustomDataGridToolbar }}
        slotProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true }
          }
        }}
        sx={dataGridSx(pallet)}
      />
    </Box>
  );
};

export default PendingFeeReportTable;
